import React, { useEffect, useState } from 'react'
import Container from 'elements/Container'
import FullCalendar, { EventClickArg, EventSourceInput } from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import CalendarModal from '../../components/CalendarModal'
import axios from 'axios'
import { isMobile } from 'react-device-detect'
import { DateTimeFormatOptions } from 'luxon'
import { calendarEvents } from './events'
import { EventFlexer, Flexer, Square, StyledWrapper } from './styled'
import { CalendarDate } from './schedule.interface'

const dayHeaderStyle = ({ text }: { text: string }) => {
  const dayTexts = text.split(' ')
  return (
    <>
      <div
        style={{
          fontWeight: 500,
          fontSize: '18px',
          lineHeight: '23px',
          color: 'white'
        }}
      >
        {dayTexts[0]}
      </div>
      <div style={{ paddingTop: '13px', color: '#8c8c8c' }}>{dayTexts[1]}</div>
    </>
  )
}

const Schedule = (): JSX.Element => {
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [events, setEvents] = useState<EventSourceInput>([])
  const [image, setImage] = useState<string>('')
  const [modalTitle, setModalTitle] = useState<string>('')
  const [eventDate, setEventDate] = useState<CalendarDate>({ formatted: '', realDate: new Date() })
  const [lastEventDate, setLastEventDate] = useState<CalendarDate>({ formatted: '', realDate: new Date() })
  const [eventDesc, setEventDesc] = useState<string>('')

  const curr = new Date()
  const first = curr.getDate() - curr.getDay()
  const last = first + 6

  const [firstCalendarDate, setFirstCalendarDate] = useState(
    new Date(curr.setDate(first)).toISOString().split('T')[0] + 'T' + '00%3A00%3A00%2B08%3A00'
  )
  const [lastCalendarDate, setLastCalendarDate] = useState(
    new Date(curr.setDate(last)).toISOString().split('T')[0] + 'T' + '00%3A00%3A00%2B08%3A00'
  )

  const calendarEventClick = (info: EventClickArg) => {
    info.jsEvent.preventDefault()

    const unparsedData = info.event.extendedProps.description

    if (!unparsedData) return

    if (!info.event.start || !info.event.end) return

    const eventDesc = unparsedData.substr(0, unparsedData.indexOf('{')).replace(/<\/?[^>]+(>|$)/g, '')
    const parsedData = unparsedData.substring(unparsedData.indexOf('{') + 1, unparsedData.lastIndexOf('}')).split(',')

    if (parsedData.length !== 2) return

    const patt = /href="(.*)"/g

    const imgUrl = parsedData[0].match(patt)
    let realImgUrl = parsedData[0]

    if (imgUrl !== null) {
      realImgUrl = imgUrl[0]
        .split('=')[1]
        .split(' ')[0]
        .replace('"', '')
        .replace('"', '')
    }

    const dateOptions: DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }

    setImage(realImgUrl)
    setModalTitle(info.event.title)
    setEventDate({
      formatted: new Date(info.event.start).toLocaleDateString('en-US', dateOptions),
      realDate: info.event.start
    })
    setLastEventDate({
      formatted: new Date(info.event.end).toLocaleDateString('en-US', dateOptions),
      realDate: info.event.end
    })
    setEventDesc(eventDesc)
    setOpenModal(true)
  }

  useEffect(() => {
    const aFunc = async () => {
      // const url = `https://www.googleapis.com/calendar/v3/calendars/boxyguild%40gmail.com/events?key=AIzaSyAS4sFg1FJYqeyID8WrQJws4F9avZGEY08&timeMin=2021-11-07T00%3A00%3A00%2B08%3A00&timeMax=2021-11-14T00%3A00%3A00%2B08%3A00&singleEvents=true&maxResults=9999`
      const url = `https://www.googleapis.com/calendar/v3/calendars/boxyguild%40gmail.com/events?key=AIzaSyCyZTLRvlTn0-oLLp3nRdTgl8m5sMcF19k&timeMin=${firstCalendarDate}&timeMax=${lastCalendarDate}&singleEvents=true&maxResults=9999`

      const { data } = await axios.get(url)

      const events = data.items.map((event: any) => {
        const unparsedData = event.description || ''

        const parsedData = unparsedData
          .substring(unparsedData.indexOf('{') + 1, unparsedData.lastIndexOf('}'))
          .split(',')

        let color

        if (parsedData[1] === 'stream') color = '#E12F2F'
        if (parsedData[1] === 'charity') color = '#814EED'
        if (parsedData[1] === 'community') color = '#D06CFF'
        if (parsedData[1] === 'event') color = '#21AC69'

        return {
          title: event.summary,
          start: event.start.dateTime,
          end: event.end.dateTime,
          description: event.description,
          color: unparsedData && color
        }
      })

      setEvents(events)
    }

    aFunc()
  }, [firstCalendarDate, lastCalendarDate])

  return (
    <Container>
      <StyledWrapper>
        <Flexer>
          <div
            style={{
              fontWeight: 500,
              fontSize: '24px'
            }}
          >
            Event Calendar
          </div>
          <EventFlexer>
            {calendarEvents.map(event => (
              <div key={event.name} style={{ display: 'flex' }}>
                <Square color={event.color} />
                <div style={{ marginRight: '16px' }}>{event.name}</div>
              </div>
            ))}
          </EventFlexer>
        </Flexer>
        <FullCalendar
          plugins={[timeGridPlugin]}
          initialView={isMobile ? 'timeGrid' : 'timeGridWeek'}
          dayCount={isMobile ? 1 : 7}
          dayHeaderFormat={{ day: 'numeric', weekday: 'long' }}
          dayHeaderContent={dayHeaderStyle}
          allDaySlot={false}
          slotDuration="02:00"
          slotMinTime="01:00"
          slotLabelFormat={{ hour: 'numeric', minute: '2-digit', hour12: false }}
          titleFormat={{
            month: 'long',
            year: 'numeric'
          }}
          events={events}
          eventClick={calendarEventClick}
          headerToolbar={{
            start: 'title prev,next',
            end: ''
          }}
          datesSet={info => {
            setFirstCalendarDate(info.startStr.replace(':', '%3A').replace('+', '%2B'))
            setLastCalendarDate(info.endStr.replace(':', '%3A').replace('+', '%2B'))
          }}
        />
        <CalendarModal
          open={openModal}
          handleToggle={() => {
            setOpenModal(prev => !prev)
          }}
          image={image}
          title={modalTitle}
          date={eventDate}
          endDate={lastEventDate}
          description={eventDesc}
        />
      </StyledWrapper>
    </Container>
  )
}

export default Schedule
